/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    global: {
        // base header height without the message bar
        '@property --header-content-height': {
            syntax: '"<length>"',
            inherits: 'false',
            initialValue: '0px'
        },
        // checkout header height
        '@property --header-checkout-height': {
            syntax: '"<length>"',
            inherits: 'false',
            initialValue: '0px'
        },
        // message bar height if available
        '@property --header-message-bar-height': {
            syntax: '"<length>"',
            inherits: 'false',
            initialValue: '0px'
        },
        // full header height - content and message bar
        // does not change from showing/hiding header
        '@property --header-height': {
            syntax: '"<length>"',
            inherits: 'true',
            initialValue: '0px'
        },
        // static subnavigation height - used on pdp
        '@property --subnavigation-height': {
            syntax: '"<length>"',
            inherits: 'true',
            initialValue: '0px'
        },
        // dynamic subnavigation height - used on plp
        '@property --dynamic-subnavigation-height': {
            syntax: '"<length>"',
            inherits: 'false',
            initialValue: '0px'
        },
        // max container width
        '@property --max-container-width': {
            syntax: '"<length>"',
            inherits: 'false',
            initialValue: '100vw'
        },
        // max-max container width
        '@property --max-viewport-width': {
            syntax: '"<length>"',
            inherits: 'true',
            initialValue: '2560px'
        },
        ':root': {
            '--base-header-content-height': {
                base: '4.25rem', // 68px
                lg: '5.5rem', // 88px
                xl: '6rem' // 96px
            },
            '--header-content-height':
                'calc(var(--base-header-content-height) * var(--has-header, 0))',
            '--header-message-bar-height': 'calc(2.25rem * var(--header-has-message-bar, 0))', // 36px
            '--header-checkout-height': 'calc(6.875rem * var(--has-checkout-header, 0))', // 110px
            '--header-height':
                'calc(var(--header-content-height) + var(--header-message-bar-height) + var(--header-checkout-height))',
            // the white space outside the max-viewport-width container
            '--max-viewport-width-offset':
                'max(0px, calc((100vw - var(--max-viewport-width)) / 2))',
            scrollBehavior: 'smooth',
            // offset the scroll top position with the header and both static (pdp) and dynamic (plp) subnavigation if any,
            // so they don't hide the scrolled to element
            scrollPaddingTop:
                'calc(var(--header-height, 0px) + var(--subnavigation-height, 0px) + var(--dynamic-subnavigation-height, 0px))',
            // offset start of content with the header and static subnavigation (pdp) if any,
            // so they don't hide the first element
            // dynamic subnavigation (plp) is not included as it's not present on page load
            paddingTop: 'calc(var(--header-height, 0px) + var(--subnavigation-height, 0px))'
        },
        // Need to fix the Google Places Autocomplete vertical position due to the additional paddingTop on the :root
        // note: alternative is to add position: relative to the body, but that might affect other absolute positioned elements
        '.pac-container': {
            marginTop: 'calc(-1 * (var(--header-height, 0px) + var(--subnavigation-height, 0px)))'
        },
        body: {
            backgroundColor: 'stokkeCore.white',
            color: 'stokkeCore.black',
            minHeight: '100vh'
        },
        '.react-target': {
            display: 'flex',
            minHeight: '100vh',
            maxWidth: 'var(--max-viewport-width)',
            marginInline: 'auto'
        },
        'b, strong': {
            fontFamily: 'stokkeSemiBold',
            fontWeight: 'semibold'
        },
        // Bottom toast message has larger margins
        // to be moved to toast.js when that can access parent toast list item
        '.chakra-toast:last-of-type': {
            mb: {
                base: 2,
                lg: 4,
                xl: 6
            }
        },
        '.sr-only': {
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            border: 0
        },
        'pwa-widget-add-to-cart': {
            display: 'block',
            // This is a hack to mimic SG - always take 400px width on desktop
            width: {lg: '25rem'}
        },
        // Remove overflow hidden from accordion collapse component, because of Spare Parts recommendations slider arrows
        // Chakra set the property directly in the element styling and if we want to change that prop with class selector we need to add an additional !important
        '.chakra-collapse': {
            overflow: 'visible!important'
        },
        // hide content in cms that is not supported in pwa
        '.cms-page-module .pwa-not-supported': {
            display: 'none !important'
        },
        // hide default browser 'X' that clears the input icons from search type inputs
        'input[type="search"]::-webkit-search-cancel-button, input[type="search"]::-webkit-search-decoration, input[type="search"]::-webkit-search-results-button, input[type="search"]::-webkit-search-results-decoration':
            {
                display: 'none'
            },
        ':focus-visible:where(:not([class^="leap"]))': {
            outline: 'none !important',
            borderRadius: '1px',
            boxShadow:
                '0 0 0 2px var(--chakra-colors-stokkeFunctional-focusBlue), 0 0 0 3px var(--chakra-colors-white) !important'
        },
        // toast position overrides as chakra doesn't allow to modify this in code
        ['#chakra-toast-manager-top-left, #chakra-toast-manager-top, #chakra-toast-manager-top-right,' +
        '#chakra-toast-manager-bottom-left, #chakra-toast-manager-bottom, #chakra-toast-manager-bottom-right']:
            {
                left: 'env(save-area-inset-left, 0px)',
                right: 'env(save-area-inset-right, 0px)',
                marginX: 'auto',
                // extend end-to-end on mobile
                alignItems: 'stretch'
            },
        '#chakra-toast-manager-top-left, #chakra-toast-manager-top, #chakra-toast-manager-top-right':
            {
                maxWidth: 'container.xxl',
                // override toast top offset to position bellow header and any subnavigation
                top:
                    'max(' +
                    // this is the default value and it's good to have it as a fallback
                    '  env(safe-area-inset-top, 0px),' +
                    '  calc(' +
                    // header height
                    '    var(--header-height, 0px) +' +
                    // subnavigation height
                    '    var(--subnavigation-height, 0px) +' +
                    // add 40px when subnavigation is visible - use clamp and round to emulate if statement
                    '    round(' +
                    '      clamp(0px, var(--subnavigation-height, 0px), 1px),' +
                    '      1px' +
                    '    ) * 40' +
                    '  )' +
                    ')' +
                    // needed to override the inline style
                    '!important'
            },
        '#chakra-toast-manager-bottom-left, #chakra-toast-manager-bottom, #chakra-toast-manager-bottom-right':
            {
                maxWidth: 'var(--max-viewport-width)'
            },
        // reset extend end-to-end on desktop
        '#chakra-toast-manager-top-left, #chakra-toast-manager-bottom-left': {
            alignItems: {lg: 'flex-start'}
        },
        '#chakra-toast-manager-top': {
            alignItems: {lg: 'center'}
        },
        '#chakra-toast-manager-bottom': {
            alignItems: {lg: 'stretch'}
        },
        '#chakra-toast-manager-top-right, #chakra-toast-manager-bottom-right': {
            alignItems: {lg: 'flex-end'}
        },
        // in the Cookiebot Admin we can change only color
        '#CybotCookiebotDialogDetailFooter a': {
            textDecoration: 'underline !important'
        },
        // in the Cookiebot Admin we can change only color of the button, not hover
        '#CybotCookiebotDialogFooter button[id*="Accept"], #CybotCookiebotDialogFooter button[id*="AllowAll"]':
            {
                '&:hover, &:focus-visible': {
                    backgroundColor: 'stokkeCore.white !important',
                    color: 'stokkeFunctional.alertGreen !important',
                    border: '1px solid stokkeFunctional.alertGreen !important',
                    opacity: '1 !important'
                }
            }
    }
}
